import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Cable, Users, Building2, UserCog, Trophy, ArrowLeftRight, BookOpen,
  Upload, Loader2, RefreshCw, CheckCircle2, AlertTriangle, Copy, Wifi, KeyRound,
} from "lucide-react";
import ApiPPPokerCapturaMac from "../components/ApiPPPokerCapturaMac";
import ApiPPPokerImportCron from "../components/ApiPPPokerImportCron";
import ApiPPPokerCarga from "../components/ApiPPPokerCarga";
import ApiPPPokerDoc from "../components/ApiPPPokerDoc";
import {
  TabJugador, TabAgentes, TabSuperUnion, TabMovimientos,
} from "../components/ApiPPPokerConsultas";
import { TabClubesUnion } from "../components/ApiPPPokerUnion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });

const TABS = [
  ["estado", "Estado", Wifi],
  ["jugador", "Jugador", Users],
  ["clubes", "Clubes de la Unión", Building2],
  ["agentes", "Agentes", UserCog],
  ["superunion", "Super Unión", Trophy],
  ["movimientos", "Movimientos", ArrowLeftRight],
  ["cargas", "Cargas manuales", Upload],
  ["carga-service", "/carga", RefreshCw],
  ["captura-mac", "Captura rdkey Mac", KeyRound],
  ["cron-importacion", "Cron importación PP", RefreshCw],
  ["doc", "Posibilidades de la API", BookOpen],
];

export default function ApiPPPoker() {
  const [tab, setTab] = useState("estado");
  const [estado, setEstado] = useState(null);
  const [loading, setLoading] = useState(false);
  const [probando, setProbando] = useState(false);
  const [clubId, setClubId] = useState(null);

  const cargarEstado = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/pppoker/consulta/estado`, auth());
      setEstado(data);
      if (!clubId) {
        // el host de la unión primero: es el único que ve los clubes de la liga
        const lista = data.clubes || [];
        const hab = lista.find((c) => c.whitelisted && /host|oficial/i.test(c.rol || ""))
          || lista.find((c) => c.whitelisted) || lista[0];
        if (hab) setClubId(hab.club_id);
      }
    } catch (e) {
      toast.error("No se pudo leer el estado de la API PPPoker");
    } finally {
      setLoading(false);
    }
  }, [clubId]);

  useEffect(() => { cargarEstado(); }, []); // eslint-disable-line

  const probar = async () => {
    if (!clubId) return;
    setProbando(true);
    try {
      const { data } = await axios.get(`${API}/pppoker/consulta/test-conexion`, {
        ...auth(), params: { club_id: clubId },
      });
      if (data.ok) toast.success(data.resumen || "Conexión OK");
      else toast.error(`${data.error} (código ${data.error_code})`);
      cargarEstado();
    } catch (e) {
      toast.error("Falló la prueba de conexión");
    } finally {
      setProbando(false);
    }
  };

  const clubes = estado?.clubes || [];

  return (
    <div className="space-y-4" data-testid="api-pppoker">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
            <Cable className="w-6 h-6 text-violet-500" /> API PPPoker
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Consultas de la API oficial y gestión de sesión nativa
          </p>
        </div>
        {!["captura-mac", "cron-importacion", "carga-service"].includes(tab) && <div className="flex flex-wrap items-end gap-2">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Club</label>
            <select value={clubId || ""} onChange={(e) => setClubId(Number(e.target.value))}
              data-testid="api-pppoker-club-select"
              className="px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600">
              {clubes.map((c) => (
                <option key={c.club_id} value={c.club_id}>
                  {c.nombre} ({c.club_id}){c.whitelisted ? "" : " · sin whitelist"}
                </option>
              ))}
            </select>
          </div>
          <button onClick={probar} disabled={probando || !clubId} data-testid="api-pppoker-probar"
            className="px-4 py-1.5 text-sm font-semibold rounded-lg bg-violet-600 hover:bg-violet-700 text-white disabled:opacity-50 flex items-center gap-2">
            {probando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wifi className="w-4 h-4" />} Probar conexión
          </button>
        </div>}
      </div>

      <div className="flex gap-1 border-b dark:border-gray-700 overflow-x-auto">
        {TABS.map(([k, label, Icon]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`api-pppoker-tab-${k}`}
            className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 whitespace-nowrap ${tab === k
              ? "border-violet-600 text-violet-600 dark:text-violet-400"
              : "border-transparent text-gray-500 hover:text-gray-800 dark:hover:text-gray-200"}`}>
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {tab === "estado" && <TabEstado estado={estado} loading={loading} onRefresh={cargarEstado} />}
      {tab === "jugador" && <TabJugador clubId={clubId} />}
      {tab === "clubes" && <TabClubesUnion clubId={clubId} />}
      {tab === "agentes" && <TabAgentes clubId={clubId} />}
      {tab === "superunion" && <TabSuperUnion clubId={clubId} />}
      {tab === "movimientos" && <TabMovimientos clubId={clubId} />}
      {tab === "cargas" && <TabCargas estado={estado} clubId={clubId} onDone={cargarEstado} />}
      {tab === "carga-service" && <ApiPPPokerCarga />}
      {tab === "captura-mac" && <ApiPPPokerCapturaMac />}
      {tab === "cron-importacion" && <ApiPPPokerImportCron />}
      {tab === "doc" && <ApiPPPokerDoc />}
    </div>
  );
}

/* ------------------------------------------------------------------ ESTADO */
const Dato = ({ label, valor, mono, testid, badge }) => (
  <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-3">
    <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
    <p className={`text-sm font-semibold text-gray-900 dark:text-gray-100 break-all ${mono ? "font-mono" : ""}`}
      data-testid={testid}>
      {valor || "—"} {badge}
    </p>
  </div>
);

function TabEstado({ estado, loading, onRefresh }) {
  if (loading && !estado) {
    return <div className="py-16 text-center text-gray-500"><Loader2 className="w-6 h-6 animate-spin mx-auto" /></div>;
  }
  if (!estado) return <div className="py-16 text-center text-gray-500">Sin datos</div>;

  const ipOk = estado.egress_ip && estado.egress_ip === estado.ip_declarada;

  return (
    <div className="space-y-4" data-testid="api-pppoker-estado">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Dato label="Endpoint" valor={estado.endpoint} mono testid="estado-endpoint" />
        <Dato label="Vendor code" valor={estado.vendor_code} mono testid="estado-vendor"
          badge={estado.es_test ? (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">TEST</span>
          ) : null} />
        <Dato label="Unión (league_id)" valor={estado.union_id} mono testid="estado-union" />
        <Dato label="IP de salida del CRM" valor={estado.egress_ip} mono testid="estado-egress-ip"
          badge={estado.egress_ip ? (
            ipOk
              ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">coincide con la declarada</span>
              : <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">≠ {estado.ip_declarada}</span>
          ) : null} />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-2 border-b dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Clubes</h2>
          <span className="text-xs text-gray-500">La whitelist de PPPoker es por IP + club</span>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-gray-50 dark:bg-gray-900/40 text-xs text-gray-500">
            <tr><th className="px-3 py-2 text-left">Club</th><th className="px-3 py-2 text-left">club_id</th>
              <th className="px-3 py-2 text-left">Rol</th><th className="px-3 py-2 text-left">Whitelist</th></tr>
          </thead>
          <tbody>
            {(estado.clubes || []).map((c) => (
              <tr key={c.club_id} className="border-t dark:border-gray-700" data-testid={`estado-club-${c.club_id}`}>
                <td className="px-3 py-2 font-medium text-gray-900 dark:text-gray-100">{c.nombre}</td>
                <td className="px-3 py-2 font-mono">{c.club_id}</td>
                <td className="px-3 py-2 text-gray-500">{c.rol}</td>
                <td className="px-3 py-2">
                  {c.whitelisted
                    ? <span className="inline-flex items-center gap-1 text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-4 h-4" /> habilitado</span>
                    : <span className="inline-flex items-center gap-1 text-amber-600 dark:text-amber-400"><AlertTriangle className="w-4 h-4" /> pendiente</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Dato label="Llamadas registradas" valor={estado.llamadas_total} testid="estado-llamadas-total" />
        <Dato label="Llamadas con error" valor={estado.llamadas_fallidas} testid="estado-llamadas-fallidas" />
        <Dato label="Eventos de webhook recibidos" valor={estado.webhook_eventos} testid="estado-webhook-eventos" />
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
        <div className="px-3 py-2 border-b dark:border-gray-700 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Últimas llamadas (con trace_id)</h2>
          <button onClick={onRefresh} data-testid="estado-refresh"
            className="text-xs px-2 py-1 rounded border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1">
            <RefreshCw className="w-3 h-3" /> Actualizar
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
              <tr>
                <th className="px-3 py-2 text-left">Cuándo</th><th className="px-3 py-2 text-left">Endpoint</th>
                <th className="px-3 py-2 text-left">Params</th><th className="px-3 py-2 text-right">ms</th>
                <th className="px-3 py-2 text-left">Resultado</th><th className="px-3 py-2 text-left">trace_id</th>
              </tr>
            </thead>
            <tbody>
              {(estado.llamadas || []).length === 0 && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">
                  Todavía no se hizo ninguna llamada. Tocá <b>Probar conexión</b>.
                </td></tr>
              )}
              {(estado.llamadas || []).map((l, i) => (
                <tr key={i} className="border-t dark:border-gray-700">
                  <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">
                    {new Date(l.ts).toLocaleString("es-AR")}
                  </td>
                  <td className="px-3 py-1.5 font-mono">{(l.path || "").replace("/poker-api/vendor", "")}</td>
                  <td className="px-3 py-1.5 font-mono text-gray-500">{JSON.stringify(l.params || {})}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{l.ms}</td>
                  <td className="px-3 py-1.5">
                    {l.ok
                      ? <span className="text-emerald-600 dark:text-emerald-400">OK</span>
                      : <span className="text-rose-600 dark:text-rose-400">{l.error_code} {l.error_message}</span>}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-gray-400">
                    {l.trace_id ? (
                      <button onClick={() => { navigator.clipboard.writeText(l.trace_id); toast.success("trace_id copiado"); }}
                        className="hover:text-violet-500 flex items-center gap-1">
                        {l.trace_id.slice(0, 12)}… <Copy className="w-3 h-3" />
                      </button>
                    ) : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ CARGAS */
function TabCargas({ estado, clubId, onDone }) {
  const [cfg, setCfg] = useState(null);
  const [uid, setUid] = useState("");
  const [monto, setMonto] = useState("");
  const [tipo, setTipo] = useState("CARGA");
  const [enviando, setEnviando] = useState(false);
  const [confirmar, setConfirmar] = useState(false);
  const [saldo, setSaldo] = useState(null);
  const [alerta, setAlerta] = useState(null);

  const cargar = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pppoker/transfer/estado`, auth());
      setCfg(data);
    } catch { /* noop */ }
  }, []);
  useEffect(() => { cargar(); }, [cargar]);

  const verSaldo = async () => {
    if (!uid || !clubId) return;
    try {
      const { data } = await axios.get(`${API}/pppoker/consulta/saldo`, { ...auth(), params: { club_id: clubId, uid } });
      if (data.ok) { setSaldo(data.data.balance); toast.success(`Saldo actual: ${data.data.balance} PP`); }
      else toast.error(data.error);
    } catch { toast.error("No se pudo leer el saldo"); }
  };

  const enviar = async () => {
    setConfirmar(false);
    setEnviando(true);
    const firmado = tipo === "CARGA" ? Number(monto) : -Number(monto);
    try {
      const { data } = await axios.post(`${API}/pppoker/transfer/enviar`,
        { club_id: clubId, uid: Number(uid), amount_pp: firmado }, auth());
      if (data.ok) {
        toast.success(`${tipo === "CARGA" ? "Cargadas" : "Retiradas"} ${monto} PP · ${data.estado} · trace ${data.trace_id?.slice(0, 10)}…`);
        setMonto(""); setSaldo(null); setAlerta(null); cargar(); onDone && onDone();
      } else {
        toast.error(`${data.estado}: ${data.error} (código ${data.error_code})`);
        if (data.alerta) setAlerta({ tipo: data.alerta, mensaje: data.error, monto });
        cargar();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail?.error || e?.response?.data?.detail || "Falló la operación");
    } finally { setEnviando(false); }
  };

  const habilitado = cfg?.envio_habilitado;
  const tope = cfg?.tope_pp ?? 1000;
  const clubWL = (cfg?.clubes || []).some((c) => Number(c.club_id) === Number(clubId));
  const clubNombre = (estado?.clubes || []).find((c) => Number(c.club_id) === Number(clubId))?.nombre || clubId;
  const montoOk = Number(monto) > 0 && Number(monto) <= tope;

  return (
    <div className="space-y-4" data-testid="api-pppoker-cargas">
      {!habilitado ? (
        <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-4">
          <p className="text-sm font-semibold text-amber-800 dark:text-amber-300 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" /> El envío de fichas está deshabilitado
          </p>
          <p className="text-sm text-amber-800/80 dark:text-amber-200/80 mt-1">
            Se activa desde <code>app_config.pppoker_api.envio_habilitado</code>.
          </p>
        </div>
      ) : (
        <>
          {alerta && (
            <div data-testid="cargas-alerta"
              className={`rounded-lg border-2 p-4 ${alerta.tipo === "CLUB_SIN_SALDO"
                ? "border-rose-500 bg-rose-50 dark:bg-rose-900/30"
                : "border-amber-400 bg-amber-50 dark:bg-amber-900/20"}`}>
              <div className="flex items-start gap-3">
                <AlertTriangle className={`w-6 h-6 shrink-0 ${alerta.tipo === "CLUB_SIN_SALDO" ? "text-rose-600 dark:text-rose-400" : "text-amber-600 dark:text-amber-400"}`} />
                <div className="flex-1">
                  <p className={`text-base font-bold ${alerta.tipo === "CLUB_SIN_SALDO" ? "text-rose-800 dark:text-rose-300" : "text-amber-800 dark:text-amber-300"}`}>
                    {alerta.tipo === "CLUB_SIN_SALDO"
                      ? `El club ${clubNombre} se quedó sin fichas`
                      : "El jugador no tiene saldo suficiente"}
                  </p>
                  <p className="text-sm text-gray-700 dark:text-gray-300 mt-1">
                    {alerta.tipo === "CLUB_SIN_SALDO"
                      ? <>PPPoker rechazó la carga de <b>{alerta.monto} PP</b> con el código <b>40001</b>. <b>No se movió ninguna ficha.</b> Hay que cargarle fichas al club desde la app y reintentar.</>
                      : <>PPPoker rechazó el retiro de <b>{alerta.monto} PP</b> con el código <b>40001</b>. No se movió ninguna ficha.</>}
                  </p>
                </div>
                <button onClick={() => setAlerta(null)} data-testid="cargas-alerta-cerrar"
                  className="text-xs text-gray-500 hover:text-gray-800 dark:hover:text-gray-200">Cerrar</button>
              </div>
            </div>
          )}

          <div className="rounded-lg border border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 p-3 text-sm text-emerald-800 dark:text-emerald-300 flex items-start gap-2">
            <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            <span>
              Operaciones habilitadas · club <b>{clubNombre}</b> {clubWL ? "(en whitelist)" : "(no habilitado)"}
              <br />
              <span className="text-xs opacity-90">
                Tope de <b>{tope} PP</b> por operación: es un <b>freno nuestro</b>, editable en la config.
                El límite real de PPPoker es de 10.000.000 PP por request. Si pedís más de lo que hay,
                PPPoker rechaza con <b>40001</b> sin mover nada: en una <b>carga</b> significa que el
                <b> club</b> se quedó sin fichas, en un <b>retiro</b> que el <b>jugador</b> no tiene.
              </span>
            </span>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 p-4 space-y-3 max-w-xl">
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Operación</p>
              <div className="flex rounded-lg overflow-hidden border dark:border-gray-600">
                <button onClick={() => setTipo("CARGA")} data-testid="cargas-tipo-carga"
                  className={`px-3 py-1 text-xs font-semibold ${tipo === "CARGA" ? "bg-emerald-600 text-white" : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>
                  Cargar
                </button>
                <button onClick={() => setTipo("DESCARGA")} data-testid="cargas-tipo-descarga"
                  className={`px-3 py-1 text-xs font-semibold ${tipo === "DESCARGA" ? "bg-rose-600 text-white" : "text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"}`}>
                  Retirar
                </button>
              </div>
            </div>
            <div className="flex flex-wrap items-end gap-2">
              <div>
                <label className="block text-xs text-gray-500 mb-1">uid del jugador</label>
                <input value={uid} onChange={(e) => { setUid(e.target.value.replace(/\D/g, "")); setSaldo(null); }}
                  placeholder="2210130" data-testid="cargas-uid"
                  className="px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 w-36" />
              </div>
              <button onClick={verSaldo} disabled={!uid} data-testid="cargas-ver-saldo"
                className="px-3 py-1.5 text-sm rounded-lg border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50">
                Ver saldo
              </button>
              <div>
                <label className="block text-xs text-gray-500 mb-1">Monto (PP)</label>
                <input value={monto} onChange={(e) => setMonto(e.target.value.replace(/[^\d.]/g, ""))}
                  placeholder="10" data-testid="cargas-monto"
                  className="px-2 py-1.5 text-sm border rounded-lg dark:bg-gray-700 dark:border-gray-600 w-28" />
              </div>
              <button onClick={() => setConfirmar(true)} disabled={enviando || !uid || !montoOk || !clubWL}
                data-testid="cargas-enviar"
                className={`px-4 py-1.5 text-sm font-semibold rounded-lg text-white disabled:opacity-50 flex items-center gap-2 ${tipo === "CARGA" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
                {enviando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {tipo === "CARGA" ? "Cargar" : "Retirar"}
              </button>
            </div>
            {saldo !== null && <p className="text-xs text-gray-500">Saldo actual del jugador: <b>{saldo} PP</b></p>}
            <p className="text-xs text-gray-500">
              El monto va en PP chips (10 = 10 PP). Cada operación queda registrada con su <code>transaction_id</code> propio;
              repetirla no se vuelve a aplicar (idempotente). <b>Retirar</b> usa monto negativo en la API — verificado y funcionando.
            </p>
            <div className="rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 p-2.5">
              <p className="text-xs text-amber-800 dark:text-amber-300">
                <b>Ojo si el uid es un agente:</b> la API carga a las <b>fichas personales</b> del usuario,
                <b> NO a la caja del agente</b> (el «Saldo de crédito» que usa para repartir a sus jugadores).
                Son dos bolsas distintas y la API oficial no permite elegir el destino. La caja del agente
                sólo se carga desde la app.
              </p>
            </div>
          </div>

          {confirmar && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="cargas-confirm-modal">
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-5 space-y-4">
                <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                  {tipo === "CARGA" ? "Confirmar carga real" : "Confirmar retiro real"}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Vas a <b>{tipo === "CARGA" ? "enviar" : "RETIRAR"}</b> <b>{monto} PP</b>
                  {tipo === "CARGA" ? " al " : " del "}jugador <b>{uid}</b> en <b>{clubNombre}</b>.
                  Es una operación real e irreversible sobre fichas.
                </p>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setConfirmar(false)} data-testid="cargas-cancelar"
                    className="px-4 py-1.5 text-sm rounded-lg border dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">Cancelar</button>
                  <button onClick={enviar} data-testid="cargas-confirmar"
                    className={`px-4 py-1.5 text-sm font-semibold rounded-lg text-white ${tipo === "CARGA" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-rose-600 hover:bg-rose-700"}`}>
                    Sí, {tipo === "CARGA" ? "cargar" : "retirar"} {monto} PP
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700 overflow-hidden">
            <div className="px-3 py-2 border-b dark:border-gray-700">
              <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Últimas operaciones</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-gray-50 dark:bg-gray-900/40 text-gray-500">
                  <tr>
                    <th className="px-3 py-2 text-left">Cuándo</th><th className="px-3 py-2 text-left">Tipo</th>
                    <th className="px-3 py-2 text-left">Club</th>
                    <th className="px-3 py-2 text-left">uid</th><th className="px-3 py-2 text-right">PP</th>
                    <th className="px-3 py-2 text-left">Estado</th><th className="px-3 py-2 text-left">Operador</th>
                    <th className="px-3 py-2 text-left">transaction_id</th>
                  </tr>
                </thead>
                <tbody>
                  {(cfg?.ultimas || []).length === 0 && (
                    <tr><td colSpan={8} className="px-3 py-6 text-center text-gray-500">Sin operaciones todavía.</td></tr>
                  )}
                  {(cfg?.ultimas || []).map((t) => (
                    <tr key={t.transaction_id} className="border-t dark:border-gray-700" data-testid={`carga-row-${t.transaction_id}`}>
                      <td className="px-3 py-1.5 whitespace-nowrap text-gray-500">{new Date(t.created_at).toLocaleString("es-AR")}</td>
                      <td className="px-3 py-1.5">
                        <span className={t.amount_pp < 0 ? "text-rose-600 dark:text-rose-400" : "text-emerald-600 dark:text-emerald-400"}>
                          {t.amount_pp < 0 ? "RETIRO" : "CARGA"}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 font-mono">{t.club_id}</td>
                      <td className="px-3 py-1.5 font-mono">{t.uid}</td>
                      <td className={`px-3 py-1.5 text-right tabular-nums font-semibold ${t.amount_pp < 0 ? "text-rose-600 dark:text-rose-400" : ""}`}>{t.amount_pp}</td>
                      <td className="px-3 py-1.5">
                        <span className={
                          t.estado === "CONFIRMADA" ? "text-emerald-600 dark:text-emerald-400"
                            : t.estado === "REQUIERE_VERIFICACION" ? "text-amber-600 dark:text-amber-400"
                              : t.estado === "PENDIENTE" ? "text-gray-500" : "text-rose-600 dark:text-rose-400"}>
                          {t.estado}
                        </span>
                      </td>
                      <td className="px-3 py-1.5 text-gray-500">{t.operador}</td>
                      <td className="px-3 py-1.5 font-mono text-gray-400">{t.transaction_id}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
